import type { Blueprint } from '../blueprint/blueprint.class';
import { BlueprintContextOptions } from './types';
import { ControllerContext } from '../../controller-classes/controller-context.class';
import { ComponentCategories, Components, ComponentVendors } from '../../component-classes';
import { IdentifierManager } from '../shared-classes/identifier-manager.class';
import type { PriceBook } from '../../price-book-classes';
import { PriceBookResolutionError } from '../kitchens/errors';

export class BlueprintContext extends ControllerContext {
	readonly blueprint: Blueprint | null;
	readonly userEmail: string;
	readonly categories: ComponentCategories;
	readonly vendors: ComponentVendors;
	readonly identifierManager: IdentifierManager;

	#priceBook: PriceBook | null;
	#components: Components;

	constructor (options: Partial<BlueprintContextOptions>) {
		super(options);
		const { blueprint, userEmail, priceBook } = options;

		this.blueprint = blueprint ?? null;
		this.userEmail = userEmail ?? 'UNKNOWN';
		this.#priceBook = priceBook ?? null;
		this.#components = this.#buildComponents(this.#priceBook);
		this.categories = new ComponentCategories({ userEmail: this.userEmail });
		this.vendors = new ComponentVendors({ userEmail: this.userEmail });
		this.identifierManager = new IdentifierManager();
	}

	// Components falls back to the live HEAD singleton on an empty dataIn, so an empty snapshot must be fatal here
	#buildComponents (priceBook: PriceBook | null): Components {
		const componentsData = priceBook?.toComponentsData() ?? null;

		if (priceBook && componentsData?.length === 0) {
			throw new PriceBookResolutionError({
				kind: 'missing-version',
				versionId: priceBook.versionId,
				versionName: priceBook.versionName,
				message: `price book version "${priceBook.versionName}" holds no component records -- refusing to price against the live catalog`
			});
		}

		return componentsData
			? new Components({ userEmail: this.userEmail, dataIn: componentsData })
			: new Components({ userEmail: this.userEmail });
	}

	// Item binds its catalog row once, in its constructor, so a swapped collection only reaches the
	// estimate after every placed item has been repointed at it.
	#rebindItems (): void {
		(this.blueprint?.kitchens?.children ?? [])
			.flatMap(({ design }) => [...(design?.islands?.children ?? []), ...(design?.obstructions?.children ?? [])])
			.flatMap((island) => island.panels?.children ?? [])
			.flatMap((panel) => panel.panelItemGroups?.children ?? [])
			.flatMap((group) => group.items?.children ?? [])
			.forEach((panelItem) => { panelItem.item?.rebindComponent(); });
	}

	get priceBook (): PriceBook | null { return this.#priceBook }
	get components (): Components { return this.#components }

	/**
	 * Repoints the whole tree at another published snapshot after construction. The designer picks a
	 * version from a chip on a design that is already open, and rebuilding the Blueprint would throw
	 * away the controller, the viewport and any unsaved edit — so the catalog is swapped underneath it
	 * instead. Islands and countertops resolve their price per read, but an Item caches its catalog
	 * row, so the placed items are rebound and the memoized cache dropped before returning — without
	 * that the estimate is a blend of two versions.
	 * @param priceBook the snapshot to price against, or null to return to the live catalog
	 */
	public applyPriceBook (priceBook: PriceBook | null): void {
		if ((this.#priceBook?.versionId ?? null) === (priceBook?.versionId ?? null)) return;

		const rebuilt = this.#buildComponents(priceBook);

		// Assigned only after the build succeeds: a snapshot that fails validation must leave the
		// context on the catalog it was already pricing against, not half-swapped.
		this.#priceBook = priceBook;
		this.#components = rebuilt;

		this.#rebindItems();
		this.cache.clear();
	}
}
